import { useState } from 'react';
import { useRootState } from './hooks/usePersistedState.js';
import { DEFAULT_RULES } from './game/engine.js';
import { TableMode } from './modes/TableMode.jsx';
import { CountDrillMode } from './modes/CountDrillMode.jsx';
import { StrategyDrillMode } from './modes/StrategyDrillMode.jsx';
import { LessonsMode } from './modes/LessonsMode.jsx';
import { SettingsDrawer } from './components/SettingsDrawer.jsx';

const DEFAULT_STATE = {
  bankroll: 1000,
  stats: {
    hands: 0, wins: 0, pushes: 0, blackjacks: 0, surrenders: 0,
    decisions: 0, correct: 0,
  },
  rules: DEFAULT_RULES,
  prefs: {
    showCount: false,
    coachOn: true,
    mode: 'table',
    onboardingDone: false,
  },
  countDrill: { runs: 0, perfect: 0, totalError: 0, bestStreak: 0, currentStreak: 0 },
  strategyDrill: { total: 0, correct: 0, byCategory: {} },
  lessons: { completed: [] },
};

const MODES = [
  { id: 'table', label: 'Table' },
  { id: 'count', label: 'Count Drill' },
  { id: 'strategy', label: 'Strategy Drill' },
  { id: 'lessons', label: 'Lessons' },
];

export default function App() {
  const [state, setState] = useRootState(DEFAULT_STATE);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // First-visit nudge into Lessons mode.
  const showOnboarding = !state.prefs.onboardingDone && state.prefs.mode === 'table' && state.stats.hands === 0;

  const setMode = (mode) => setState((s) => ({ ...s, prefs: { ...s.prefs, mode } }));
  const setRules = (rules) => setState((s) => ({ ...s, rules }));
  const setPrefs = (prefs) => setState((s) => ({ ...s, prefs }));
  const setBankroll = (next) => setState((s) => ({ ...s, bankroll: typeof next === 'function' ? next(s.bankroll) : next }));
  const setStats = (next) => setState((s) => ({ ...s, stats: typeof next === 'function' ? next(s.stats) : next }));
  const setCountStats = (next) => setState((s) => ({ ...s, countDrill: typeof next === 'function' ? next(s.countDrill) : next }));
  const setStratStats = (next) => setState((s) => ({ ...s, strategyDrill: typeof next === 'function' ? next(s.strategyDrill) : next }));
  const setLessonProgress = (next) => setState((s) => ({ ...s, lessons: typeof next === 'function' ? next(s.lessons) : next }));

  const dismissOnboarding = () => setState((s) => ({ ...s, prefs: { ...s.prefs, onboardingDone: true } }));

  return (
    <div className="min-h-screen flex flex-col">
      <Header
        mode={state.prefs.mode}
        setMode={setMode}
        onOpenSettings={() => setSettingsOpen(true)}
      />

      <main className="flex-1 flex flex-col">
        {showOnboarding && state.prefs.mode === 'table' && (
          <OnboardingBanner
            onStart={() => { setMode('lessons'); dismissOnboarding(); }}
            onDismiss={dismissOnboarding}
          />
        )}

        {state.prefs.mode === 'table' && (
          <TableMode
            rules={state.rules}
            prefs={state.prefs}
            bankroll={state.bankroll}
            setBankroll={setBankroll}
            stats={state.stats}
            setStats={setStats}
          />
        )}

        {state.prefs.mode === 'count' && (
          <CountDrillMode drillStats={state.countDrill} setDrillStats={setCountStats} />
        )}

        {state.prefs.mode === 'strategy' && (
          <StrategyDrillMode stats={state.strategyDrill} setStats={setStratStats} prefs={state.prefs} />
        )}

        {state.prefs.mode === 'lessons' && (
          <LessonsMode
            progress={state.lessons}
            setProgress={setLessonProgress}
            onJumpTo={setMode}
          />
        )}
      </main>

      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        rules={state.rules}
        setRules={setRules}
        prefs={state.prefs}
        setPrefs={setPrefs}
        onResetBankroll={() => { setBankroll(1000); setSettingsOpen(false); }}
      />

      <footer className="text-center text-white/40 text-xs py-4 px-3 border-t border-white/5 space-y-1">
        <div>BlackjackTrainer v0.2 · Stats persist locally · Reset bankroll in Settings</div>
        <div className="text-white/30 max-w-2xl mx-auto leading-relaxed">
          Educational tool only. Simulated chips, no real money, not gambling. Card counting is legal in most jurisdictions but
          casinos may refuse service to suspected counters. Practice helps you understand the math, not beat any specific casino.
        </div>
      </footer>
    </div>
  );
}

function Header({ mode, setMode, onOpenSettings }) {
  // Short labels on mobile, full labels on sm+
  const SHORT = { table: 'Table', count: 'Count', strategy: 'Strategy', lessons: 'Lessons' };
  return (
    <header className="bg-black/40 backdrop-blur border-b border-white/10 sticky top-0 z-20">
      <div className="flex items-center justify-between gap-3 px-3 sm:px-6 py-2 sm:py-3">
        <h1 className="font-display text-lg sm:text-2xl font-bold tracking-wide whitespace-nowrap">
          <span className="sm:hidden">21</span>
          <span className="hidden sm:inline">Blackjack Trainer</span>
        </h1>

        <nav className="flex gap-1 flex-1 justify-center overflow-x-auto scrollbar-thin">
          {MODES.map((m) => (
            <button key={m.id} onClick={() => setMode(m.id)}
              className={`px-2 sm:px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition ${
                mode === m.id ? 'bg-chip text-black' : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}>
              {SHORT[m.id]}
            </button>
          ))}
        </nav>

        <button onClick={onOpenSettings}
          className="text-white/60 hover:text-white transition p-1.5 rounded hover:bg-white/10 shrink-0"
          aria-label="Settings">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
      </div>
    </header>
  );
}

function OnboardingBanner({ onStart, onDismiss }) {
  return (
    <div className="bg-chip/10 border-b border-chip/30 px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
      <div className="text-sm">
        <b className="text-chip">First time?</b> Walk through the lessons before sitting down. They take ~10 minutes and teach you the whole game.
      </div>
      <div className="flex gap-2">
        <button onClick={onStart} className="px-4 py-1.5 bg-chip text-black font-bold text-sm rounded">
          Start lessons
        </button>
        <button onClick={onDismiss} className="px-4 py-1.5 text-white/60 hover:text-white text-sm">
          Skip
        </button>
      </div>
    </div>
  );
}
